import { httpsCallable } from "firebase/functions";

import { auth, functions } from "@/firebase/authorityClient";
import { isAccountAdministrationFeatureEnabled } from "@/config/runtimeConfig";
import {
  UNIT_AMOUNT_SCALE,
  isCanonicalNonnegativeAtomic,
} from "@/utils/atomicCurrencyFormatting";
import { withUiDeadline } from "@/utils/uiDeadline";

// Client for docs/03-apis/account-lifecycle-administration.md. Every request
// is rebuilt from validated primitives and every response is checked against
// the exact contract shape before a component can render or act on it.

const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const ORG_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const MEMBERSHIP_REF_PATTERN = /^[a-f0-9]{64}$/;
const PUBLIC_IDENTIFIER_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/;
const IMPACT_REF_PATTERN = /^[a-f0-9]{64}$/;
const CONTROL_CHARACTERS =
  /[\u0000-\u001f\u007f-\u009f؜‎‏‪-‮⁦-⁩]/;

const LONG_RUNNING_CALLABLES = new Set(["deleteUserAccount", "deleteOwnUserAccount"]);
const LONG_RUNNING_CALLABLE_TIMEOUT_MS = 9 * 60_000;

export const ACCOUNT_DELETION_STAGES = Object.freeze([
  "checking",
  "locking",
  "money",
  "items",
  "messages",
  "following",
  "identity",
  "memberships",
  "content",
  "profile",
  "sign-in",
]);
const DELETION_PROGRESS_STATES = new Set(["unknown", "running", "completed", "failed"]);

export const ACCOUNTS_PAGE_SIZE = 20;
export const SUCCESSOR_CANDIDATES_PAGE_SIZE = 10;
const MAX_ACCOUNT_QUERY_LENGTH = 120;
const MAX_CANDIDATE_QUERY_LENGTH = 80;
const MAX_CURSOR_LENGTH = 1024;
const MAX_IMPACT_ORGANISATIONS = 500;
const MAX_SUCCESSORS_PER_ORGANISATION = 25;

export const ACCOUNT_ADMINISTRATION_REASONS = Object.freeze([
  "impact-changed",
  "impact-review-required",
  "organisation-role-limit",
  "successor-required",
  "successor-invalid",
  "organisation-federated",
  "organisation-sibling-relationships",
  "open-monetary-quote",
  "account-not-found",
  "self-deletion-forbidden",
  "membership-wallet-not-empty",
  "feature-disabled",
]);
const REASONS = new Set(ACCOUNT_ADMINISTRATION_REASONS);

const REASON_MESSAGES = Object.freeze({
  "impact-changed":
    "Balances or memberships changed since this review. Check the updated details and confirm again.",
  "impact-review-required":
    "Review what will be deleted before you confirm.",
  "organisation-role-limit":
    "The admin role in this organisation is full. Choose fewer new admins.",
  "successor-required":
    "Nominate at least one new admin for every organisation that needs one.",
  "successor-invalid":
    "A nominated admin is no longer an active member. Choose another member.",
  "organisation-federated":
    "A federated organisation cannot be closed or left this way yet.",
  "organisation-sibling-relationships":
    "An organisation still has sibling relationships. Remove them before it can close.",
  "open-monetary-quote":
    "A payment is still in progress. Wait for it to finish, then try again.",
  "account-not-found": "This account no longer exists.",
  "self-deletion-forbidden":
    "You cannot delete your own account from administration. Use Account settings instead.",
  "membership-wallet-not-empty":
    "Your Wallet in this organisation still holds money. Spend or transfer it before leaving.",
  "feature-disabled": "This action is not available yet.",
});

const CODE_MESSAGES = Object.freeze({
  aborted: "This action conflicts with an earlier attempt. Reload and try again.",
  "deadline-exceeded": "The account service did not respond in time.",
  "failed-precondition": "This action cannot be completed right now.",
  "invalid-argument": "The request was invalid.",
  "invalid-response": "The account service returned an invalid response.",
  "not-found": "The account or organisation is unavailable.",
  "permission-denied": "You do not have permission to do this.",
  "resource-exhausted": "Too many requests. Wait a moment and try again.",
  "stale-auth-context":
    "The signed-in account changed. Reload the page and try again.",
  "ui-deadline": "The account service did not respond in time.",
  unauthenticated: "Sign in again to continue.",
  unavailable: "The account service is temporarily unavailable.",
});

export class AccountAdministrationServiceError extends Error {
  constructor(code = "unknown", message, reason = null) {
    super(
      message ||
        (reason && REASON_MESSAGES[reason]) ||
        CODE_MESSAGES[code] ||
        "The request could not be completed."
    );
    this.name = "AccountAdministrationServiceError";
    this.code = code;
    this.reason = REASONS.has(reason) ? reason : null;
  }
}

const fail = (code, message, reason = null) => {
  throw new AccountAdministrationServiceError(code, message, reason);
};

export function describeAccountAdministrationError(error) {
  if (error?.reason && REASON_MESSAGES[error.reason]) {
    return REASON_MESSAGES[error.reason];
  }
  if (error instanceof AccountAdministrationServiceError) return error.message;
  return "The request could not be completed.";
}

function isPlainRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactKeys(value, keys) {
  if (!isPlainRecord(value)) return false;
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return (
    actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
  );
}

function requireResponseShape(value, keys) {
  if (!hasExactKeys(value, keys)) fail("invalid-response");
  return value;
}

function normalizeOperationId(value) {
  if (typeof value !== "string" || !OPERATION_ID_PATTERN.test(value)) {
    fail("invalid-argument", "The account operation is invalid.");
  }
  return value;
}

function normalizeUid(value, code = "invalid-argument") {
  if (
    typeof value !== "string" ||
    value.length < 1 ||
    value.length > 128 ||
    value !== value.trim() ||
    CONTROL_CHARACTERS.test(value)
  ) {
    fail(code);
  }
  return value;
}

function normalizeOrgId(value, code = "invalid-argument") {
  if (typeof value !== "string" || !ORG_ID_PATTERN.test(value)) fail(code);
  return value;
}

function normalizeMembershipRef(value, code = "invalid-argument") {
  if (typeof value !== "string" || !MEMBERSHIP_REF_PATTERN.test(value)) {
    fail(code);
  }
  return value;
}

function normalizeImpactRef(value, code = "invalid-argument") {
  if (typeof value !== "string" || !IMPACT_REF_PATTERN.test(value)) fail(code);
  return value;
}

function normalizeRevision(value, code = "invalid-argument") {
  if (!Number.isSafeInteger(value) || value < 1) fail(code);
  return value;
}

function normalizeCursor(value, code = "invalid-argument") {
  if (value === null || value === undefined) return null;
  if (
    typeof value !== "string" ||
    value.length < 1 ||
    value.length > MAX_CURSOR_LENGTH ||
    CONTROL_CHARACTERS.test(value)
  ) {
    fail(code);
  }
  return value;
}

function normalizeQuery(value, maximum) {
  if (value === null || value === undefined) return "";
  if (typeof value !== "string") fail("invalid-argument");
  const query = value.trim();
  if (query.length > maximum || CONTROL_CHARACTERS.test(query)) {
    fail("invalid-argument", "The search is too long.");
  }
  return query;
}

function normalizeResponseText(value, { maximum, nullable = false }) {
  if (nullable && value === null) return null;
  if (
    typeof value !== "string" ||
    value.trim().length < 1 ||
    value.length > maximum ||
    CONTROL_CHARACTERS.test(value)
  ) {
    fail("invalid-response");
  }
  return value.trim();
}

function normalizeTimestamp(value, { nullable = false } = {}) {
  if (nullable && value === null) return null;
  if (!Number.isSafeInteger(value) || value < 0) fail("invalid-response");
  return value;
}

function normalizePage(value, hasMore) {
  if (typeof hasMore !== "boolean") fail("invalid-response");
  if (hasMore) return normalizeCursor(value, "invalid-response") || fail("invalid-response");
  if (value !== null) fail("invalid-response");
  return null;
}

function normalizeSuccessorCandidate(value) {
  requireResponseShape(value, [
    "membershipRef",
    "displayName",
    "publicIdentifier",
    "joinedAt",
    "isAdmin",
  ]);
  if (
    typeof value.isAdmin !== "boolean" ||
    (value.publicIdentifier !== null &&
      (typeof value.publicIdentifier !== "string" ||
        !PUBLIC_IDENTIFIER_PATTERN.test(value.publicIdentifier)))
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    membershipRef: normalizeMembershipRef(value.membershipRef, "invalid-response"),
    displayName: normalizeResponseText(value.displayName, { maximum: 160 }),
    publicIdentifier: value.publicIdentifier,
    joinedAt: normalizeTimestamp(value.joinedAt),
    isAdmin: value.isAdmin,
  });
}

function normalizeCurrency(value) {
  requireResponseShape(value, ["name", "symbol", "code"]);
  const symbol = normalizeResponseText(value.symbol, { maximum: 16 });
  if (Array.from(symbol).length > 8) fail("invalid-response");
  return Object.freeze({
    name: normalizeResponseText(value.name, { maximum: 80 }),
    symbol,
    code: normalizeResponseText(value.code, { maximum: 16, nullable: true }),
  });
}

function normalizeOrganisationImpact(value) {
  requireResponseShape(value, [
    "orgId",
    "name",
    "currency",
    "amountScale",
    "currentAtomic",
    "savingsAtomic",
    "willClose",
    "needsSuccessor",
    "suggestedSuccessor",
  ]);
  if (
    value.amountScale !== UNIT_AMOUNT_SCALE ||
    !isCanonicalNonnegativeAtomic(value.currentAtomic) ||
    !isCanonicalNonnegativeAtomic(value.savingsAtomic) ||
    typeof value.willClose !== "boolean" ||
    typeof value.needsSuccessor !== "boolean" ||
    (value.willClose && value.needsSuccessor) ||
    (value.willClose && value.suggestedSuccessor !== null)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    orgId: normalizeOrgId(value.orgId, "invalid-response"),
    name: normalizeResponseText(value.name, { maximum: 160 }),
    currency: normalizeCurrency(value.currency),
    amountScale: UNIT_AMOUNT_SCALE,
    currentAtomic: value.currentAtomic,
    savingsAtomic: value.savingsAtomic,
    willClose: value.willClose,
    needsSuccessor: value.needsSuccessor,
    suggestedSuccessor:
      value.suggestedSuccessor === null
        ? null
        : normalizeSuccessorCandidate(value.suggestedSuccessor),
  });
}

function normalizeSubject(value) {
  requireResponseShape(value, ["uid", "email", "displayName"]);
  return Object.freeze({
    uid: normalizeUid(value.uid, "invalid-response"),
    email: normalizeResponseText(value.email, { maximum: 320, nullable: true }),
    displayName: normalizeResponseText(value.displayName, {
      maximum: 160,
      nullable: true,
    }),
  });
}

function normalizeDeletionPreview(value) {
  requireResponseShape(value, [
    "schemaVersion",
    "subject",
    "organisations",
    "impactRef",
  ]);
  if (
    value.schemaVersion !== 1 ||
    !Array.isArray(value.organisations) ||
    value.organisations.length > MAX_IMPACT_ORGANISATIONS
  ) {
    fail("invalid-response");
  }
  const organisations = value.organisations.map(normalizeOrganisationImpact);
  if (new Set(organisations.map(({ orgId }) => orgId)).size !== organisations.length) {
    fail("invalid-response");
  }
  return Object.freeze({
    schemaVersion: 1,
    subject: normalizeSubject(value.subject),
    organisations: Object.freeze(organisations),
    impactRef: normalizeImpactRef(value.impactRef, "invalid-response"),
  });
}

function normalizeAccountRow(value) {
  requireResponseShape(value, [
    "uid",
    "email",
    "displayName",
    "lastSignInAt",
    "disabled",
    "activeMembershipCount",
  ]);
  if (
    typeof value.disabled !== "boolean" ||
    !Number.isSafeInteger(value.activeMembershipCount) ||
    value.activeMembershipCount < 0
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    uid: normalizeUid(value.uid, "invalid-response"),
    email: normalizeResponseText(value.email, { maximum: 320, nullable: true }),
    displayName: normalizeResponseText(value.displayName, {
      maximum: 160,
      nullable: true,
    }),
    lastSignInAt: normalizeTimestamp(value.lastSignInAt, { nullable: true }),
    disabled: value.disabled,
    activeMembershipCount: value.activeMembershipCount,
  });
}

function normalizeSuccessorsMap(value) {
  if (value === undefined || value === null) return Object.freeze({});
  if (!isPlainRecord(value)) fail("invalid-argument");
  const entries = Object.entries(value).map(([orgId, refs]) => {
    normalizeOrgId(orgId);
    if (
      !Array.isArray(refs) ||
      refs.length < 1 ||
      refs.length > MAX_SUCCESSORS_PER_ORGANISATION
    ) {
      fail(
        "invalid-argument",
        REASON_MESSAGES["successor-required"],
        "successor-required"
      );
    }
    const normalized = refs.map((ref) => normalizeMembershipRef(ref));
    if (new Set(normalized).size !== normalized.length) {
      fail("invalid-argument", REASON_MESSAGES["successor-invalid"], "successor-invalid");
    }
    return [orgId, Object.freeze(normalized)];
  });
  return Object.freeze(Object.fromEntries(entries));
}

function normalizeSuccessorRefs(value) {
  if (value === undefined || value === null) return Object.freeze([]);
  if (!Array.isArray(value) || value.length > MAX_SUCCESSORS_PER_ORGANISATION) {
    fail("invalid-argument");
  }
  const refs = value.map((ref) => normalizeMembershipRef(ref));
  if (new Set(refs).size !== refs.length) {
    fail("invalid-argument", REASON_MESSAGES["successor-invalid"], "successor-invalid");
  }
  return Object.freeze(refs);
}

function normalizeDeletedResponse(value, outcomes = ["deleted"]) {
  requireResponseShape(value, ["schemaVersion", "outcome"]);
  if (value.schemaVersion !== 2 || !outcomes.includes(value.outcome)) {
    fail("invalid-response");
  }
  return Object.freeze({ schemaVersion: 2, outcome: value.outcome });
}

function captureActor(authInstance) {
  const actor = authInstance?.currentUser;
  if (
    !actor ||
    typeof actor.uid !== "string" ||
    actor.uid.length < 1 ||
    actor.uid.length > 128 ||
    CONTROL_CHARACTERS.test(actor.uid)
  ) {
    fail("unauthenticated");
  }
  return actor;
}

function assertActorIsCurrent(authInstance, actor) {
  if (
    authInstance?.currentUser !== actor ||
    authInstance.currentUser?.uid !== actor.uid
  ) {
    fail("stale-auth-context");
  }
}

function normalizeServiceError(error) {
  if (error instanceof AccountAdministrationServiceError) return error;
  const candidate = String(error?.code || "unknown")
    .split("/")
    .pop();
  const code = Object.hasOwn(CODE_MESSAGES, candidate) ? candidate : "unknown";
  const reason = REASONS.has(error?.details?.reason)
    ? error.details.reason
    : null;
  return new AccountAdministrationServiceError(code, undefined, reason);
}

export function createAccountAdministrationOperationId(
  prefix = "account_admin",
  cryptoProvider = globalThis.crypto
) {
  if (!/^[A-Za-z][A-Za-z0-9_-]{2,24}$/.test(prefix)) {
    fail("invalid-argument", "The account operation is invalid.");
  }
  if (typeof cryptoProvider?.randomUUID === "function") {
    const candidate = `${prefix}_${cryptoProvider
      .randomUUID()
      .replaceAll("-", "_")}`;
    if (OPERATION_ID_PATTERN.test(candidate)) return candidate;
  }
  if (typeof cryptoProvider?.getRandomValues === "function") {
    const bytes = cryptoProvider.getRandomValues(new Uint8Array(24));
    const candidate = `${prefix}_${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0")
    ).join("")}`;
    if (OPERATION_ID_PATTERN.test(candidate)) return candidate;
  }
  fail("failed-precondition", "Secure account operations are unavailable.");
}

export function createAccountAdministrationService({
  functionsInstance = functions,
  authInstance = auth,
  callableFactory = httpsCallable,
  enabled = isAccountAdministrationFeatureEnabled,
} = {}) {
  const callables = new Map();
  // Account deletion runs for minutes on large accounts. The Firebase client
  // aborts a callable after 70 seconds unless told otherwise, which would
  // report a failure while the server is still deleting.
  const callableFor = (name) => {
    if (!callables.has(name)) {
      callables.set(
        name,
        LONG_RUNNING_CALLABLES.has(name)
          ? callableFactory(functionsInstance, name, {
              timeout: LONG_RUNNING_CALLABLE_TIMEOUT_MS,
            })
          : callableFactory(functionsInstance, name)
      );
    }
    return callables.get(name);
  };

  const requireEnabled = () => {
    if (!enabled) {
      fail("feature-disabled", undefined, "feature-disabled");
    }
  };

  const invoke = async ({ name, request, phase, timeoutMs = 30_000, actor }) => {
    const caller = actor || captureActor(authInstance);
    try {
      const response = await withUiDeadline(callableFor(name)(request), {
        phase,
        timeoutMs,
      });
      assertActorIsCurrent(authInstance, caller);
      return response?.data;
    } catch (error) {
      assertActorIsCurrent(authInstance, caller);
      throw normalizeServiceError(error);
    }
  };

  return Object.freeze({
    async listAccounts({ query = "", cursor = null } = {}) {
      requireEnabled();
      const request = Object.freeze({
        schemaVersion: 1,
        query: normalizeQuery(query, MAX_ACCOUNT_QUERY_LENGTH),
        cursor: normalizeCursor(cursor),
      });
      const value = await invoke({
        name: "listAccountsForAdministration",
        request,
        phase: "listing accounts",
      });
      requireResponseShape(value, [
        "schemaVersion",
        "accounts",
        "nextCursor",
        "hasMore",
      ]);
      if (
        value.schemaVersion !== 1 ||
        !Array.isArray(value.accounts) ||
        value.accounts.length > ACCOUNTS_PAGE_SIZE
      ) {
        fail("invalid-response");
      }
      const accounts = value.accounts.map(normalizeAccountRow);
      if (new Set(accounts.map(({ uid }) => uid)).size !== accounts.length) {
        fail("invalid-response");
      }
      return Object.freeze({
        accounts: Object.freeze(accounts),
        nextCursor: normalizePage(value.nextCursor, value.hasMore),
        hasMore: value.hasMore,
      });
    },

    async previewAccountDeletion(target) {
      requireEnabled();
      let normalizedTarget;
      if (hasExactKeys(target, ["uid"])) {
        normalizedTarget = Object.freeze({ uid: normalizeUid(target.uid) });
      } else if (hasExactKeys(target, ["orgId", "membershipRef"])) {
        normalizedTarget = Object.freeze({
          orgId: normalizeOrgId(target.orgId),
          membershipRef: normalizeMembershipRef(target.membershipRef),
        });
      } else {
        fail("invalid-argument");
      }
      const value = await invoke({
        name: "previewAccountDeletion",
        request: Object.freeze({ schemaVersion: 1, target: normalizedTarget }),
        phase: "previewing account deletion",
      });
      const preview = normalizeDeletionPreview(value);
      if (normalizedTarget.uid && preview.subject.uid !== normalizedTarget.uid) {
        fail("invalid-response");
      }
      return preview;
    },

    async previewOwnAccountDeletion() {
      requireEnabled();
      const actor = captureActor(authInstance);
      const value = await invoke({
        name: "previewOwnAccountDeletion",
        request: Object.freeze({ schemaVersion: 1 }),
        phase: "previewing account deletion",
        actor,
      });
      const preview = normalizeDeletionPreview(value);
      if (preview.subject.uid !== actor.uid) fail("invalid-response");
      return preview;
    },

    async previewOwnOrganisationExit(orgId) {
      requireEnabled();
      const request = Object.freeze({
        schemaVersion: 1,
        orgId: normalizeOrgId(orgId),
      });
      const value = await invoke({
        name: "previewOwnOrganisationExit",
        request,
        phase: "previewing organisation exit",
      });
      requireResponseShape(value, [
        "schemaVersion",
        "organisation",
        "membershipRevision",
        "impactRef",
      ]);
      if (value.schemaVersion !== 1) fail("invalid-response");
      const organisation = normalizeOrganisationImpact(value.organisation);
      if (organisation.orgId !== request.orgId) fail("invalid-response");
      return Object.freeze({
        schemaVersion: 1,
        organisation,
        membershipRevision: normalizeRevision(
          value.membershipRevision,
          "invalid-response"
        ),
        impactRef: normalizeImpactRef(value.impactRef, "invalid-response"),
      });
    },

    async listSuccessorCandidates({
      orgId,
      subjectUid = null,
      query = "",
      cursor = null,
    } = {}) {
      requireEnabled();
      const request = Object.freeze({
        schemaVersion: 1,
        orgId: normalizeOrgId(orgId),
        subjectUid: subjectUid === null ? null : normalizeUid(subjectUid),
        query: normalizeQuery(query, MAX_CANDIDATE_QUERY_LENGTH),
        cursor: normalizeCursor(cursor),
      });
      const value = await invoke({
        name: "listSuccessorCandidates",
        request,
        phase: "listing successor candidates",
      });
      requireResponseShape(value, [
        "schemaVersion",
        "candidates",
        "nextCursor",
        "hasMore",
      ]);
      if (
        value.schemaVersion !== 1 ||
        !Array.isArray(value.candidates) ||
        value.candidates.length > SUCCESSOR_CANDIDATES_PAGE_SIZE
      ) {
        fail("invalid-response");
      }
      const candidates = value.candidates.map(normalizeSuccessorCandidate);
      if (
        new Set(candidates.map(({ membershipRef }) => membershipRef)).size !==
        candidates.length
      ) {
        fail("invalid-response");
      }
      return Object.freeze({
        candidates: Object.freeze(candidates),
        nextCursor: normalizePage(value.nextCursor, value.hasMore),
        hasMore: value.hasMore,
      });
    },

    // uid is given when an administrator follows a deletion they started;
    // omitted when an account holder follows their own.
    async getAccountDeletionProgress({ uid = null, operationId } = {}) {
      requireEnabled();
      const request = Object.freeze({
        schemaVersion: 1,
        ...(uid === null ? {} : { uid: normalizeUid(uid) }),
        operationId: normalizeOperationId(operationId),
      });
      const value = await invoke({
        name: "getAccountDeletionProgress",
        request,
        phase: "reading deletion progress",
        timeoutMs: 15_000,
      });
      requireResponseShape(value, [
        "schemaVersion",
        "state",
        "stage",
        "stageIndex",
        "stageCount",
        "reason",
      ]);
      if (
        value.schemaVersion !== 1 ||
        !DELETION_PROGRESS_STATES.has(value.state) ||
        !(value.stage === null || ACCOUNT_DELETION_STAGES.includes(value.stage)) ||
        !Number.isSafeInteger(value.stageIndex) ||
        !Number.isSafeInteger(value.stageCount) ||
        value.stageIndex < 0 ||
        value.stageIndex > value.stageCount ||
        !(value.reason === null || typeof value.reason === "string")
      ) {
        fail("invalid-response");
      }
      return Object.freeze({
        state: value.state,
        stage: value.stage,
        stageIndex: value.stageIndex,
        stageCount: value.stageCount,
        reason: REASONS.has(value.reason) ? value.reason : null,
      });
    },

    async deleteUserAccount({
      uid,
      impactRef,
      successors,
      operationId = createAccountAdministrationOperationId("delete_user"),
    } = {}) {
      requireEnabled();
      const actor = captureActor(authInstance);
      const request = Object.freeze({
        schemaVersion: 2,
        uid: normalizeUid(uid),
        operationId: normalizeOperationId(operationId),
        impactRef: normalizeImpactRef(impactRef),
        successors: normalizeSuccessorsMap(successors),
      });
      if (request.uid === actor.uid) {
        fail("failed-precondition", undefined, "self-deletion-forbidden");
      }
      const value = await invoke({
        name: "deleteUserAccount",
        request,
        phase: "deleting account",
        timeoutMs: 8 * 60_000,
        actor,
      });
      return normalizeDeletedResponse(value);
    },

    async deleteOwnAccount({
      impactRef,
      successors,
      operationId = createAccountAdministrationOperationId("delete_own"),
    } = {}) {
      requireEnabled();
      const request = Object.freeze({
        schemaVersion: 2,
        operationId: normalizeOperationId(operationId),
        impactRef: normalizeImpactRef(impactRef),
        successors: normalizeSuccessorsMap(successors),
      });
      const value = await invoke({
        name: "deleteOwnUserAccount",
        request,
        phase: "deleting account",
        timeoutMs: 8 * 60_000,
      });
      // A retried self-deletion with the same operation reports "replayed".
      return normalizeDeletedResponse(value, ["deleted", "replayed"]);
    },

    async leaveOrganisation({
      orgId,
      expectedRevision,
      impactRef,
      successorMembershipRefs,
      operationId = createAccountAdministrationOperationId("leave_org"),
    } = {}) {
      requireEnabled();
      const request = Object.freeze({
        schemaVersion: 2,
        operationId: normalizeOperationId(operationId),
        expectedRevision: normalizeRevision(expectedRevision),
        orgId: normalizeOrgId(orgId),
        impactRef: normalizeImpactRef(impactRef),
        successorMembershipRefs: normalizeSuccessorRefs(successorMembershipRefs),
      });
      const value = await invoke({
        name: "leaveOrganisation",
        request,
        phase: "leaving organisation",
        timeoutMs: 60_000,
      });
      requireResponseShape(value, [
        "schemaVersion",
        "orgId",
        "outcome",
        "membershipStatus",
        "membershipRevision",
      ]);
      if (
        value.schemaVersion !== 1 ||
        value.orgId !== request.orgId ||
        !["left", "closed"].includes(value.outcome) ||
        value.membershipStatus !== "inactive" ||
        !Number.isSafeInteger(value.membershipRevision) ||
        value.membershipRevision <= request.expectedRevision
      ) {
        fail("invalid-response");
      }
      return Object.freeze({
        orgId: value.orgId,
        outcome: value.outcome,
        membershipStatus: "inactive",
        membershipRevision: value.membershipRevision,
      });
    },
  });
}

export const accountAdministrationService = createAccountAdministrationService();
